import { useState, useEffect, useCallback } from 'react';
import { consultationTypesAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Pencil, Trash2, Clock, IndianRupee } from 'lucide-react';

const CATEGORIES = ['General', 'Specialist', 'Procedure', 'Surgery', 'Emergency', 'Tele-consult', 'Follow-up'];
const COLORS = ['#0D9488', '#0EA5E9', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981', '#6366F1'];

const ConsultationTypesPage = () => {
  const { hospital } = useAuth();
  const hospitalId = hospital?.id;
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const res = await consultationTypesAPI.list(params);
      setTypes(res.data || []);
    } catch { toast.error('Failed to load consultation types'); }
    setLoading(false);
  }, [hospitalId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm('Delete this consultation type?')) return;
    try { await consultationTypesAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="consultation-types-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Consultation Types</h1>
          <p className="text-sm text-slate-500 mt-0.5">{types.length} types configured · used in appointment booking</p>
        </div>
        <Button onClick={() => { setEditItem(null); setShowModal(true); }} className="bg-teal-600 hover:bg-teal-700" data-testid="add-consultation-type-btn">
          <Plus size={16} className="mr-1.5" /> Add Type
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-slate-400">Loading...</p>
        ) : types.length === 0 ? (
          <div className="col-span-full text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">
            No consultation types configured. Add your first type to enable categorized bookings.
          </div>
        ) : types.map(ct => (
          <div key={ct.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`consultation-type-${ct.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${ct.color}20`, color: ct.color }}>
                  <span className="font-bold text-sm">{ct.name?.[0] || '?'}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{ct.name}</h3>
                  <p className="text-xs text-slate-500">{ct.category}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditItem(ct); setShowModal(true); }} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`edit-ct-${ct.id}`}>
                  <Pencil size={14} className="text-slate-400" />
                </button>
                <button onClick={() => handleDelete(ct.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-ct-${ct.id}`}>
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600 pt-3 border-t border-slate-100">
              <span className="flex items-center gap-1"><Clock size={13} />{ct.duration_minutes}m</span>
              <span className="flex items-center gap-1"><IndianRupee size={13} />{ct.price?.toLocaleString() || 0}</span>
              {ct.code && <span className="text-xs font-mono text-slate-400">{ct.code}</span>}
            </div>
            {ct.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{ct.description}</p>}
            <div className="flex gap-1.5 mt-2">
              {ct.requires_referral && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Referral</span>}
              {ct.requires_pre_auth && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">Pre-Auth</span>}
            </div>
          </div>
        ))}
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} editItem={editItem} hospitalId={hospitalId} onSaved={load} />
    </div>
  );
};

const Modal = ({ open, onClose, editItem, hospitalId, onSaved }) => {
  const [form, setForm] = useState({ name: '', code: '', category: 'General', duration_minutes: 30, price: 0, color: '#0D9488', description: '', requires_referral: false, requires_pre_auth: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editItem) setForm({
      name: editItem.name || '', code: editItem.code || '', category: editItem.category || 'General',
      duration_minutes: editItem.duration_minutes || 30, price: editItem.price || 0,
      color: editItem.color || '#0D9488', description: editItem.description || '',
      requires_referral: !!editItem.requires_referral, requires_pre_auth: !!editItem.requires_pre_auth,
    });
    else setForm({ name: '', code: '', category: 'General', duration_minutes: 30, price: 0, color: '#0D9488', description: '', requires_referral: false, requires_pre_auth: false });
  }, [editItem, open]);

  const save = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration_minutes: parseInt(form.duration_minutes) || 30, price: parseFloat(form.price) || 0, hospital_id: hospitalId };
      if (editItem) await consultationTypesAPI.update(editItem.id, payload);
      else await consultationTypesAPI.create(payload);
      toast.success('Saved'); onSaved(); onClose();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editItem ? 'Edit Type' : 'Add Consultation Type'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cardiology Follow-up" data-testid="ct-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="CARDIO-FU" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} /></div>
            <div><Label>Price (INR)</Label><Input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></div>
          </div>
          <div>
            <Label>Color</Label>
            <div className="flex gap-2 mt-1">
              {COLORS.map(c => (
                <button key={c} onClick={() => setForm({ ...form, color: c })} className={`h-7 w-7 rounded-full border-2 ${form.color === c ? 'border-slate-900' : 'border-transparent'}`} style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="flex items-center justify-between py-2 border-t border-slate-100">
            <Label className="text-sm">Requires Referral</Label>
            <Switch checked={form.requires_referral} onCheckedChange={v => setForm({ ...form, requires_referral: v })} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Requires Pre-Authorization</Label>
            <Switch checked={form.requires_pre_auth} onCheckedChange={v => setForm({ ...form, requires_pre_auth: v })} />
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-ct-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationTypesPage;
